/**
 * Extract a human-readable error message from an API/axios error
 * and return structured details including the error code.
 *
 * getApiError returns an object: { message: string, code?: number|string }
 *
 * For backward compatibility, getApiErrorMessage still returns only the message string.
 */

function extractMessage(error, fallbackMessage) {
  try {
    if (typeof error === "string") {
      const trimmed = error.trim();
      return trimmed || fallbackMessage;
    }

    const response =
      error && typeof error === "object" && error.response
        ? error.response
        : null;
    const data =
      response && typeof response === "object" ? response.data : null;

    if (typeof data === "string" && data.trim()) {
      return data.trim();
    }

    if (data && typeof data === "object") {
      if (typeof data.message === "string" && data.message.trim()) {
        return data.message.trim();
      }

      if (typeof data.error === "string" && data.error.trim()) {
        return data.error.trim();
      }
      if (data.error && typeof data.error === "object") {
        if (
          typeof data.error.message === "string" &&
          data.error.message.trim()
        ) {
          return data.error.message.trim();
        }
      }

      if (Array.isArray(data.errors) && data.errors.length > 0) {
        const messages = data.errors
          .map((e) => {
            if (!e) return null;
            if (typeof e === "string") return e.trim();
            if (typeof e.message === "string") return e.message.trim();
            return null;
          })
          .filter(Boolean);
        if (messages.length > 0) {
          return messages.join("\n");
        }
      }

      if (typeof data.detail === "string" && data.detail.trim()) {
        return data.detail.trim();
      }
    }

    if (
      error &&
      typeof error === "object" &&
      typeof error.message === "string" &&
      error.message.trim()
    ) {
      return error.message.trim();
    }
  } catch (_) {
    // ignore and fall through
  }

  return fallbackMessage;
}

function extractCode(error) {
  try {
    const response =
      error && typeof error === "object" && error.response
        ? error.response
        : null;
    const status =
      response && typeof response.status === "number"
        ? response.status
        : undefined;
    if (typeof status === "number") return status;
    const code = error && typeof error === "object" ? error.code : undefined;
    return code;
  } catch (_) {
    return undefined;
  }
}

export function getApiError(
  error,
  fallbackMessage = "An unexpected error occurred"
) {
  const message = extractMessage(error, fallbackMessage);
  const code = extractCode(error);
  return { message, code };
}

/**
 * Backward-compatible helper that returns only the error message string.
 */
export function getApiErrorMessage(
  error,
  fallbackMessage = "An unexpected error occurred"
) {
  return extractMessage(error, fallbackMessage);
}
